import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http'
import { Subject } from 'rxjs';
import { Config } from '../Config';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements AfterViewInit {

  constructor(private http: HttpClient) { }

  @ViewChild('map', { static: false }) private readonly mapRef: ElementRef<any>;
  @ViewChild('extent', { static: false }) private readonly extentRef: ElementRef<any>
  @ViewChild('basemapSwitcher', { static: false }) private readonly basemapSwitcherRef: ElementRef<any>
  @ViewChild('videoLogViewer', { static: false }) private readonly videoLogViewer: ElementRef<any>;
  @ViewChild('videoLogController', { static: false }) private readonly videoLogController: ElementRef<any>;
  @ViewChild('routeSelectorWindow', { static: false }) private readonly routeSelectorWindow: ElementRef<any>;
  @ViewChild('videoLogInfo', { static: false }) private readonly videoLogInfo: ElementRef<any>;
  @ViewChild('endSegmentWarningWindow', { static: false }) private readonly endSegmentWarningWindow: ElementRef<any>;
  @ViewChild('endCountyWarningWindow', { static: false }) private readonly endCountyWarningWindow: ElementRef<any>;
  @ViewChild('beginSegmentWarningWindow', { static: false }) private readonly beginSegmentWarningWindow: ElementRef<any>;
  @ViewChild('beginCountyWarningWindow', { static: false }) private readonly beginCountyWarningWindow: ElementRef<any>;
  @ViewChild('historicReportNoticeWindow', { static: false }) private readonly historicReportNoticeWindow: ElementRef<any>;
  @ViewChild('yearWarningWindow', { static: false }) private readonly yearWarningWindow: ElementRef<any>;


  public config = (window as any).config as Config;

  basemapList = 'terrain,streets,satellite,hybrid,topo,national-geographic,gray-vector,osm';
  defaultBasemap = {
    "tileUrl": "https://gis.penndot.pa.gov/gis/rest/services/basemaps/penndotbasemap/MapServer/tile",
    "thumbnailTile": "/7/48/36",
    "tileTemplate": "/{level}/{row}/{col}",
    "title": "PennDOT"
  };
  basemapConfigs: any[] = [
    {
      "tileUrl": "https://services.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer/tile",
      "thumbnailTile": "/15/12358/9365",
      "tileTemplate": "/{level}/{row}/{col}",
      "title": "Elevation"
    },
    this.defaultBasemap,
    {
      "tileUrl": "https://services.arcgisonline.com/arcgis/rest/services/USA_Topo_Maps/MapServer/tile",
      "thumbnailTile": "/7/48/36",
      "tileTemplate": "/{level}/{row}/{col}",
      "title": "Topographic DRG"
    }
  ];

  extentConfigs: any[] = [
    {
      toolTip: "Statewide",
      icon: "m28.531 12.656-.187-.531-1.156-.344-.344-.562-.187-1.437-.719-.312-.094-.344-.375-.312h-20.219v-1.625l-1.656.844 .219-.312-.656.5-1.375.594-.406 13.906 20.719.094h1.719l.313-.531.5-.25.781 .156 1.375-.562.094-.437 1.75-1.312-1.187-1.437h-.25l-.25-.875-.531-.062-.031-.75v-.375l.406-.25.188-.469-.375-.75.844-.844.719-1.25z",
      color: "white",
      extent: {
        center: [-77.6362661695295, 40.92039887644528],
        zoom: 8
      },
      extentType: "static",
      label: "Statewide Extent"
    },
    {
      toolTip: "Current Area of Interest",
      icon: "icon-ui-locate",
      color: "white",
      extent: async () => await this.getAoi(),
      extentType: "async-func",
      label: "AOI"
    }
  ];

  tabs: any[] = [
    { "name": "Basic Info", "active": true },
    { "name": "Segment Data" },
    { "name": "Admin Data" }, 
    { "name": "Pavement Conditions" },
    { "name": "Shoulder Conditions" },
    { "name": "Guiderail Conditions" }
  ];

  jurisMap = {
    1: 'State',
    2: 'Turnpike',
    3: 'Agency Owned',
    5: 'Non-State Federal Aid Roads',
    6: 'Toll Bridges',
    8: 'Local Roads'
  };

  segmentTitle: string = "";
  lat: number;
  long: number;
  crsList: { countyCode: string, direction: string, distance: number, latLong:{latitude:number, longitude:number}, offset:string, routeName:string, segmentNumber:string, stateRouteNumber:string }[];
  localCRSList: { countyCode: string, countyIXN: string, intersectionType: string, stateRouteIXN: string, stateRouteNumber: string, streetName: string, streetNameIXN: string }[];
  showCRS: boolean = false;
  showLocalINX: boolean = false;

  userClosed: boolean = false;
  isShowingImage: boolean = false;
  isPopUp: boolean = false;
  isInternal: boolean = false;
  isLoading: boolean;
  adminData: any;
  segmentData: any[];
  pavementData: any;
  pavementType: "Bituminous" | "Concrete" | "Reinforced" | "Unpaved" = 'Bituminous';
  shoulderData: any;
  guiderailData: any;
  curTab: number = 0;
  curSegment: string;
  lastAOIName: string = "";
  aoiIndex: number = 0;
  isStartup: boolean = true;
  showMileMarkers: boolean = false;
  historicRptYear: number = 0;
  showHistoricRptWarningYear: boolean = false;

  closeAoiWindow: Subject<void> = new Subject<void>();
  openStreetViewWindow: Subject<{ lat: number; long: number; }> = new Subject<{ lat: number; long: number; }>();

  rptType: string = "basicRpt";

  updateRMSLinkParams: { cn: string, rt: string, seg: string } = { cn: '', rt: '', seg: '' };
  layerService: any;

  userAgent: string = navigator.userAgent;
  verOffset: number = this.userAgent.indexOf("MSIE");
  isIE: boolean = this.verOffset > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./);

  async ngAfterViewInit() {
    this.setBasemap();
    this.setupBasemapSwitcher();
    this.constructExtent();

    this.videoLogViewer.nativeElement.active = true;
    this.videoLogViewer.nativeElement.directionalMarker = true;
    this.videoLogViewer.nativeElement.toolTipText = 'Double-Click to view a bigger image.';

    // set player defaults if defined
    if (this.config.playerSetting) {
      if (this.config.playerSetting.minSpeed) {
        this.videoLogController.nativeElement.VideoLogService.playerSetting.minSpeed = this.config.playerSetting.minSpeed;
      }
      if (this.config.playerSetting.maxSpeed) {
        this.videoLogController.nativeElement.VideoLogService.playerSetting.maxSpeed = this.config.playerSetting.maxSpeed;
      }
      if (this.config.playerSetting.playerSpeed) {
        this.videoLogController.nativeElement.VideoLogService.playerSpeed = this.config.playerSetting.playerSpeed;
      }
      if (this.config.playerSetting.step) {
        this.videoLogController.nativeElement.VideoLogService.playerSetting.step = this.config.playerSetting.step;
      }
      if (this.config.playerSetting.fullsizeImages !== undefined) {
        this.videoLogController.nativeElement.VideoLogService.toggleFullsizeImages(this.config.playerSetting.fullsizeImages);
      }
    }

    if (window.location.href.includes("isPopUp=true")) {
      //for pop-up window, hide nav-bar, info & map components
      this.isPopUp = true;
      this.videoLogViewer.nativeElement.enablePopUpWindow = false; //prevent more popup windows
      this.videoLogViewer.nativeElement.toolTipText = ""; //hide tooltip
      var url = new URL(window.location.href);
      var CRS: { countyCode: string, stateRouteNumber: string, segmentNumber: string, offset: string } = { countyCode: '', stateRouteNumber: '', segmentNumber: '', offset: '' };
      CRS.countyCode = url.searchParams.get("cn");
      CRS.stateRouteNumber = url.searchParams.get("rt");
      CRS.segmentNumber = url.searchParams.get("sg");
      CRS.offset = url.searchParams.get("offset");

      await this.videoLogViewer.nativeElement.setCrs(CRS);
      this.videoLogController.nativeElement.VideoLogService.toggleFullsizeImages(true);

      if (url.searchParams.get('pos')) {
        this.videoLogViewer.nativeElement.swapPanels(true);
      }

      this.videoLogController.nativeElement.toggleButtonsForPopup();

      if (this.videoLogController.nativeElement.VideoLogService.yearRange) {
        var yearRangeMax: number = this.videoLogController.nativeElement.VideoLogService.yearRange.years[this.videoLogController.nativeElement.VideoLogService.yearRange.yearIndexMax];
        var year: number = +(this.getParameterByName('year'));

        if (year !== yearRangeMax) {
          this.videoLogController.nativeElement.VideoLogService.toggleHistoricVideoLog();
          await this.sleep(250);
          this.videoLogController.nativeElement.VideoLogService.currentYear = year;
        }
      }
    }

    const paramArray = window.location.search.substring(1).split("&");
   
    if (paramArray.length == 1 && (window.location.href.includes("streetView=true") || window.location.href.includes("streetview=true") || window.location.href.includes("sv=true"))) {
      let startLat = this.lat ? this.lat : 40.263001;
      let startLong = this.long ? this.long : -76.884089;
      this.openStreetViewWindow.next({ lat: startLat, long: startLong });
      this.isStartup = false;
    }
    
    this.videoLogViewer.nativeElement.addEventListener('positionChange', data => {
      this.lat = data.detail.lat;
      this.long = data.detail.long;
      this.crsList = data.detail.crsList;
      this.localCRSList = data.detail.localCRSList;
      this.userClosed = false;
      if (this.crsList) {
        this.setRouteSelectorVisibility();
      }

      if ((window.location.href.includes("streetView=true") || window.location.href.includes("streetview=true") || window.location.href.includes("sv=true")) && this.isStartup) {
        let startLat = this.lat ? this.lat : 40.263001;
        let startLong = this.long ? this.long : -76.884089;
        this.openStreetViewWindow.next({ lat: startLat, long: startLong });
        this.isStartup = false;
      }
     
      const cn = this.videoLogController.nativeElement.VideoLogService.videoLog.countyCode ? this.videoLogController.nativeElement.VideoLogService.videoLog.countyCode : null;
      const route = this.videoLogController.nativeElement.VideoLogService.videoLog.stateRouteNumber ? this.videoLogController.nativeElement.VideoLogService.videoLog.stateRouteNumber : null;
      const seg = this.videoLogController.nativeElement.VideoLogService.videoLog.segmentNumber ? this.videoLogController.nativeElement.VideoLogService.videoLog.segmentNumber : null;
      
      this.aoiIndex = 0;
      var count = 0;
      if (this.lastAOIName && this.lastAOIName !== "") {
        const lastAOIArr = this.lastAOIName.split('|');
        const cty = lastAOIArr[0] ? lastAOIArr[0] : "";
        const rt = lastAOIArr[1] ? lastAOIArr[1] : "";
        const segStart = lastAOIArr[2] ? lastAOIArr[2] : "";

        for (const crs of this.crsList) {
          if (crs.countyCode === cty) {
            if (rt === "") {
              this.aoiIndex = count;
              break;
            } else if (crs.stateRouteNumber === rt) {
              if (segStart === "") {
                this.aoiIndex = count;
                break;
              } else if (crs.segmentNumber === segStart) {
                this.aoiIndex = count;
                break;
              }
            }
          }
          count++;
        }
      } else if (cn && route && seg) {
        for (const crs of this.crsList) {
          if (crs.countyCode === cn && crs.stateRouteNumber === route && crs.segmentNumber === seg) {
            this.aoiIndex = count;
            break;
          }
          count++;
        }
      }


      //on each position change, if the new segment is different from the past one, update reports
      if (this.curSegment !== undefined && this.curSegment !== this.crsList[this.aoiIndex].segmentNumber) {

        this.log('New Segment Selected', 3, false);

        this.updateReports();

        this.curSegment = this.crsList[this.aoiIndex].segmentNumber;

      } else if (this.curSegment === undefined) {
        this.curSegment = this.crsList[this.aoiIndex].segmentNumber;
      }


      //update nav-bar with link params
      if (this.crsList != null) {
        this.updateRMSLinkParams = { cn: this.crsList[this.aoiIndex].countyCode, rt: this.crsList[this.aoiIndex].stateRouteNumber, seg: this.crsList[this.aoiIndex].segmentNumber };
      }

      this.lastAOIName = "";
      this.isShowingImage = true;
    });

    this.videoLogViewer.nativeElement.addEventListener('loadHistoricReport', () => {
      this.updateReports();
    });

    this.videoLogController.nativeElement.addEventListener('loadHistoricReport', () => {
      this.updateReports();
    });

    this.videoLogViewer.nativeElement.addEventListener('openPopUp', data => {
      //open pop up window based on current location
      let vlLink = `${window.location.origin}${window.location.pathname}?isPopUp=true`;
      vlLink += this.crsList[this.aoiIndex].countyCode ? '&cn=' + this.crsList[this.aoiIndex].countyCode : '';
      vlLink += this.crsList[this.aoiIndex].stateRouteNumber ? '&rt=' + this.crsList[this.aoiIndex].stateRouteNumber : '';
      vlLink += this.crsList[this.aoiIndex].segmentNumber ? '&sg=' + this.crsList[this.aoiIndex].segmentNumber : '';
      vlLink += this.crsList[this.aoiIndex].offset ? '&offset=' + this.videoLogController.nativeElement.VideoLogService.image.offset : '';
      vlLink += data.detail ? '&pos=' + data.detail : '';

      const yr = this.videoLogController.nativeElement.VideoLogService.currentYear;
      vlLink += yr ? '&year=' + yr : '';
      window.open(vlLink, '', 'width=1000,height=550');
    });

    this.routeSelectorWindow.nativeElement.addEventListener('openChange', open => {
      if(!open.detail) {
        this.userClosed = true;
      } 
    })

    this.videoLogController.nativeElement.addEventListener('promptSegmentEnd', () => {
      //display end of segment dialogue
      this.endSegmentWarningWindow.nativeElement.open();
    });
    this.videoLogController.nativeElement.addEventListener('promptCountyEnd', () => {
      //display end of county dialogue
      this.endCountyWarningWindow.nativeElement.open();
    });
    this.videoLogController.nativeElement.addEventListener('promptSegmentBegin', () => {
      //display start of segment dialogue
      this.beginSegmentWarningWindow.nativeElement.open();
    });
    this.videoLogController.nativeElement.addEventListener('promptCountyBegin', () => {
      //display start of county dialogue
      this.beginCountyWarningWindow.nativeElement.open();
    });

    this.videoLogViewer.nativeElement.addEventListener("swapPanelEvent", () => {
      //swap map and image panels
      this.swapPanels();
    });

    this.mapRef.nativeElement.DeepLinkService.parameters = {

      counties: ['counties', 'countyNumbers'],
      countyNumber: ['ct', 'cn', 'cty', 'cty-code', 'county', 'countyNumber'],
      routeNumber: ['rt', 'route', 'st-rt-no', 'routeNumber'],
      segmentNumber: ['sg', 'seg', 'segment', 'segmentNumber'],
      offset: ['offset'],
      latitude: ['lat', 'latitude'],
      longitude: ['long', 'longitude'],
      query: ['query'],
      segmentNumberBegin: ['sg', 'segmentBegin', 'segmentNumberBegin', 'beginSegment', 'beginSegmentNumber', 'segment', 'segmentNumber'],
      segmentNumberEnd: ['sgend', 'segmentEnd', 'segmentNumberEnd', 'endSegment', 'endSegmentNumber'],
    };

    await this.mapRef.nativeElement.ready;
    
    this.mapRef.nativeElement.mapView.watch('zoom', (currentZoom) => {
      if (currentZoom >= 15) {
        this.videoLogViewer.nativeElement.buffer = 40;
      } else {
        this.videoLogViewer.nativeElement.buffer = currentZoom * 150;
      }
    });

    this.layerService = this.mapRef.nativeElement.LayerService;

    if (this.mapRef.nativeElement.DeepLinkService.deepLinkParamIsPresent()) {

      var handler = this.mapRef.nativeElement.map.layers.on('after-add', (e) => {
        if (e.item) {
          handler.remove();
        }
      });

      //close the aoi window if it is open, since a location is already specified
      this.closeAoiWindow.next();

      //nothing in videologservice/viewer we can await for to see if viewer is loaded, so just wait 2 seconds instead
      var waitTime = 2000;
      waitTime += window.location.search.includes('sg') ? 200 : 0;
      await this.sleep(waitTime);
      this.updateLocationToAoi(); //update selected location to aoi location

      if (window.location.search.includes('year')) { //if there is a year deeplink, update to that year
        var year: number = +(this.getParameterByName('year'));
        this.videoLogController.nativeElement.VideoLogService.toggleHistoricVideoLog();
        await this.sleep(250);
        this.videoLogController.nativeElement.VideoLogService.currentYear = year;
      }
    }

    //turn on fullsize images by default
    this.videoLogController.nativeElement.VideoLogService.toggleFullsizeImages(this.config.playerSetting.fullsizeImages);
    //turn on segment highlights by default
    this.videoLogController.nativeElement.SegmentHighlight = true;
  }

  setIsInternal(isInternal: boolean) {
    this.isInternal = isInternal;
    this.videoLogController.nativeElement.hideDownloadBtn = !this.isInternal;
  }
  toggleKeyboard(isEnabled:boolean) {
    this.videoLogController.nativeElement.VideoLogService.toggleKeyboardEnabled(isEnabled);
  }
  toggleMapClickable(isEnabled:boolean) {
    this.videoLogViewer.nativeElement.mapClickActive = isEnabled;
  }
  async updateLocationToAoi() {
    const layers = await this.layerService.getAoiLayers();
    var latLong;
    if (layers && layers.length) {
      this.lastAOIName = layers[0].mapcoreProps ? layers[0].mapcoreProps.name : this.lastAOIName;
      if (layers[0].graphics) {
        latLong = [layers[0].graphics.toArray()[0].geometry.latitude, layers[0].graphics.toArray()[0].geometry.longitude];
      } else {
        var result = await this.layerService.getLayerFeatures(layers[0], ["SEG_NO", "CTY_CODE", "ST_RT_NO"]);
        if (result) {
          latLong = result[0].geometry.startLatLong;
        }
      }
    }

    if (this.lastAOIName && (this.lastAOIName.split("|").length - 1) >= 4) {
      const loc = this.lastAOIName.split("|");
      this.videoLogController.nativeElement.VideoLogService.goToSegmentAndSetOffset(loc[0], loc[1], loc[2], loc[4]);
    }
    else if (window.location.search.includes('offset')) {
      var url_string = window.location.href;
      var url = new URL(url_string);
      var cn = url.searchParams.get("cn");
      var rt = url.searchParams.get("rt");
      var sg = url.searchParams.get("sg");
      var offset = url.searchParams.get("offset");
      if (cn && rt && sg && offset) {
        this.videoLogController.nativeElement.VideoLogService.goToSegmentAndSetOffset(cn, rt, sg, offset);
      }
    }
    else if (latLong) {
      this.videoLogViewer.nativeElement.aoiString = this.lastAOIName ? this.lastAOIName : "";
      this.videoLogViewer.nativeElement.lat = latLong[0];
      this.videoLogViewer.nativeElement.long = latLong[1];
    }
  }

  getParameterByName(name) {
    name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
    var regex = new RegExp("[\\?&]" + name + "=([^&#]*)"),
      results = regex.exec(location.search);
    return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
  }

  async constructExtent() {
    await this.extentRef.nativeElement.ready;
    for (let extentConfig of this.extentConfigs) {
      this.extentRef.nativeElement.addExtentConfiguration(extentConfig);
    }
  }

  async setBasemap() {
    await this.mapRef.nativeElement.ready;
    await this.mapRef.nativeElement.setBasemapsFromConfig(this.defaultBasemap);
  }

  async setupBasemapSwitcher() {
    await this.basemapSwitcherRef.nativeElement.ready;
    this.basemapSwitcherRef.nativeElement.addBasemaps(this.basemapList, this.basemapConfigs);
  }

  async getAoi() {
    const aoiLayer = this.mapRef.nativeElement.LayerService.getAoiLayers();
    if (aoiLayer.length) {
      const aoiExtent = await this.mapRef.nativeElement.LayerService.getLayerExtent(aoiLayer[0]);
      return aoiExtent;
    }
  }

  setRouteSelectorVisibility() {
    const crsCount = this.crsList != undefined ? this.crsList.length : 0;
    const localINXCount = this.localCRSList != undefined ? this.localCRSList.length : 0;
    const crsSelectionEnabled = this.videoLogController.nativeElement.VideoLogService.crsSelection;
    const LocalINXEnabled = this.videoLogController.nativeElement.VideoLogService.findinxlist;

    let showDialog = true;
    if (crsCount <= 2 || this.userClosed) {
      showDialog = false;
    }

    let ShowINXDialog = true;
    if (localINXCount < 1 || this.userClosed) {
      ShowINXDialog = false;
    }

    this.showCRS = crsSelectionEnabled && showDialog ? true : false;
    this.showLocalINX = LocalINXEnabled && ShowINXDialog ? true : false;

    if (this.routeSelectorWindow.nativeElement.isOpened) {
      if (!(showDialog && crsSelectionEnabled) && !(ShowINXDialog && LocalINXEnabled)) {
        this.routeSelectorWindow.nativeElement.close();
      }
    } else {
      if ((showDialog && crsSelectionEnabled) || (LocalINXEnabled && ShowINXDialog)) {
        this.routeSelectorWindow.nativeElement.open();
      }
    }
  }

  async crsSelect(crs) {
    let curYear: number = this.videoLogController.nativeElement.VideoLogService.currentYear ? this.videoLogController.nativeElement.VideoLogService.currentYear : 0;
    const latestYear: number = this.videoLogController.nativeElement.VideoLogService.yearRange.years[this.videoLogController.nativeElement.VideoLogService.yearRange.yearIndexMax];
    curYear = curYear == latestYear ? 0 : curYear;
    const hasYear: boolean = await this.videoLogController.nativeElement.VideoLogService.crsHasYear(crs, curYear.toString());
    if (hasYear) {
      this.log('Next Segment Selected.', 3, false);
      await this.videoLogViewer.nativeElement.setCrs(crs);
      if (curYear != 0) {
        this.videoLogController.nativeElement.VideoLogService.currentYear = curYear;
      }
    } else {
      this.yearWarningWindow.nativeElement.open();
    }
  }


  async onTabChange(event) {
    var tabName: string = event.detail.name;

    var selectedYear: number = this.videoLogController.nativeElement.VideoLogService.currentYear;
    var latestYear: number = this.videoLogController.nativeElement.VideoLogService.yearRange.years[this.videoLogController.nativeElement.VideoLogService.yearRange.yearIndexMax];

    if (tabName == "Basic Info") {
      this.rptType = "basicRpt";
      this.curTab = 0;

    } else if (tabName == "Segment Data") {
      this.rptType = "segRpt";
      this.curTab = 1;
      this.getSegmentData();

    } else if (tabName == "Admin Data") {
      this.rptType = "adminRpt";
      this.curTab = 2;
      this.getAdminData();

    } else if (tabName == "Pavement Conditions") {
      this.rptType = "pavementRpt";
      this.curTab = 3;
      await this.getPavementData();

      if (this.pavementData && this.pavementType) {
        let surveyDate;
        if (this.pavementType == 'Bituminous') {
          surveyDate = this.pavementData.bitSurveyDate;
        } else if (this.pavementType == 'Concrete') {
          surveyDate = this.pavementData.concSurveyDate;
        } else if (this.pavementType == 'Reinforced') {
          surveyDate = this.pavementData.crcSurveyDate;
        } else if (this.pavementType == 'Unpaved') {
          surveyDate = this.pavementData.upSurveyDate;
        }
        surveyDate = new Date(surveyDate);
        if (selectedYear < latestYear) {
          this.showHistoricReportNotice(surveyDate.getFullYear());
        }
      }

    } else if (tabName == "Shoulder Conditions") {
      this.rptType = "shoulderRpt";
      this.curTab = 4;
      await this.getShoulderData();
      
      if ((selectedYear < latestYear) && this.shoulderData) {
        let surveyDate = new Date(this.shoulderData.condDate);
        this.showHistoricReportNotice(surveyDate.getFullYear());
      }

    } else if (tabName == "Guiderail Conditions") {
      this.rptType = "guiderailRpt";
      this.curTab = 5;
      await this.getGuiderailData();
      if ((selectedYear < latestYear) && this.guiderailData.length > 0) {
        this.showHistoricReportNotice();
      }

    }
  }

  exportTable() {
    let wb: XLSX.WorkBook = XLSX.utils.book_new();
    let ws: XLSX.WorkSheet;
    
    if (this.rptType == "basicRpt") {
      let basicRptData = this.buildBasicRptExportData()
      if (basicRptData) {
        ws = XLSX.utils.json_to_sheet([basicRptData]);
        XLSX.utils.book_append_sheet(wb, ws, 'Basic Info');
        XLSX.writeFile(wb, 'VideoLog_Basic_Info.xlsx');
      }
      
    } else if (this.rptType == "segRpt" && this.segmentData) {
      ws = XLSX.utils.json_to_sheet(this.segmentData);
      XLSX.utils.book_append_sheet(wb, ws, 'Segment data');
      XLSX.writeFile(wb, 'VideoLog_Segment_Data.xlsx');

    } else if (this.rptType == "adminRpt" && this.adminData) {
      ws = XLSX.utils.json_to_sheet([this.adminData]);
      XLSX.utils.book_append_sheet(wb, ws, 'Admin data');
      XLSX.writeFile(wb, 'VideoLog_Admin_Data.xlsx');

    } else if (this.rptType == "pavementRpt" && this.pavementData) {
      ws = XLSX.utils.json_to_sheet([this.pavementData]);
      XLSX.utils.book_append_sheet(wb, ws, 'Pavement data');
      XLSX.writeFile(wb, 'VideoLog_Pavement_Data.xlsx');

    } else if (this.rptType == "shoulderRpt" && this.shoulderData) {
      ws = XLSX.utils.json_to_sheet([this.shoulderData]);
      XLSX.utils.book_append_sheet(wb, ws, 'Shoulder data');
      XLSX.writeFile(wb, 'VideoLog_Shoulder_Data.xlsx');

    } else if (this.rptType == "guiderailRpt" && this.guiderailData) {
      ws = XLSX.utils.json_to_sheet(this.guiderailData);
      XLSX.utils.book_append_sheet(wb, ws, 'Guiderail data');
      XLSX.writeFile(wb, 'VideoLog_Guiderail_Data.xlsx');

    }
  }

  async exportAllTables() {
    this.isLoading = true;

    let wb: XLSX.WorkBook = XLSX.utils.book_new();
    let ws: XLSX.WorkSheet;

    let basicInfoRptData = this.buildBasicRptExportData();
    ws = XLSX.utils.json_to_sheet([basicInfoRptData]);
    XLSX.utils.book_append_sheet(wb, ws, 'Basic Info');

    if (!this.segmentData) {
      await this.getSegmentData();
    }
    ws = XLSX.utils.json_to_sheet(this.segmentData);
    XLSX.utils.book_append_sheet(wb, ws, 'Segment data');

    if (!this.adminData) {
      await this.getAdminData();
    }
    ws = XLSX.utils.json_to_sheet([this.adminData]);
    XLSX.utils.book_append_sheet(wb, ws, 'Admin data');

    if (!this.pavementData) {
      await this.getPavementData();
    }
    ws = XLSX.utils.json_to_sheet([this.pavementData]);
    XLSX.utils.book_append_sheet(wb, ws, 'Pavement data');

    if (!this.shoulderData) {
      await this.getShoulderData();
    }
    ws = XLSX.utils.json_to_sheet([this.shoulderData]);
    XLSX.utils.book_append_sheet(wb, ws, 'Shoulder data');

    if (!this.guiderailData) {
      await this.getGuiderailData();
    }
    ws = XLSX.utils.json_to_sheet(this.guiderailData);
    XLSX.utils.book_append_sheet(wb, ws, 'Guiderail data');

    XLSX.writeFile(wb, 'VideoLog_Data.xlsx');
    this.isLoading = false;
  }

  getTableId(reportType) {
    if (reportType == 'basicRpt') {
      return 'tblBasicInfo';
    }
    else if (reportType == 'adminRpt') {
      return 'tblAdminData';
    }
    else if (reportType == 'pavementRpt') {
      return 'tblPavementData';
    }
    else if (reportType == 'shoulderRpt') {
      return 'tblShoulderData';
    }
    else if (reportType == 'guiderailRpt') {
      return 'tblGDRailData';
    }
  }

  sorterFunction(guiderailData) {
    return -parseInt(guiderailData.BgnOffset);
  }

  async updateReports() {
    var selectedYear: number = this.videoLogController.nativeElement.VideoLogService.currentYear;
    var latestYear: number = this.videoLogController.nativeElement.VideoLogService.yearRange.years[this.videoLogController.nativeElement.VideoLogService.yearRange.yearIndexMax];
    //clear data for all reports
    this.segmentData = null;
    this.adminData = null;
    this.pavementData = null;
    this.shoulderData = null;
    this.guiderailData = null;
    //fill selected report for new location
    if (this.curTab == 1) {
      this.segmentData = null;
      this.getSegmentData();
    } else if (this.curTab == 2) {
      this.adminData = null;
      this.getAdminData();
    } else if (this.curTab == 3) {
      this.pavementData = null;
      await this.getPavementData();

      if (this.pavementData && this.pavementType) {
        let surveyDate;
        if (this.pavementType == 'Bituminous') {
          surveyDate = this.pavementData.bitSurveyDate;
        } else if (this.pavementType == 'Concrete') {
          surveyDate = this.pavementData.concSurveyDate;
        } else if (this.pavementType == 'Reinforced') {
          surveyDate = this.pavementData.crcSurveyDate;
        } else if (this.pavementType == 'Unpaved') {
          surveyDate = this.pavementData.upSurveyDate;
        }
        surveyDate = new Date(surveyDate);
        if (selectedYear < latestYear) {
          this.showHistoricReportNotice(surveyDate.getFullYear());
        }
      }
    } else if (this.curTab == 4) {
      this.shoulderData = null;
      await this.getShoulderData();
      
      if ((selectedYear < latestYear) && this.shoulderData) {
        let surveyDate = new Date(this.shoulderData.condDate);
        this.showHistoricReportNotice(surveyDate.getFullYear());
      }
    } else if (this.curTab == 5) {
      this.guiderailData = null;
      await this.getGuiderailData();

      if ((selectedYear < latestYear) && this.guiderailData.length > 0) {
        this.showHistoricReportNotice();
      }
    }
  }
  async getAdminData() {
    //******************************************************need url to have enviroment handle**************************
    this.isLoading = true;
    var requestUrl = "api/reports/Admin?county=" + this.crsList[this.aoiIndex].countyCode + "&route=" + this.crsList[this.aoiIndex].stateRouteNumber
      + "&segment=" + this.crsList[this.aoiIndex].segmentNumber;
    try {
      const data: any = await this.http.get<any>(requestUrl).toPromise();
      this.adminData = data;
      this.isLoading = false;
    } catch(error)  {
        console.error('Error', error);
        this.isLoading = false;
    }
  }

  async getPavementData() {
    this.isLoading = true;
    const requestUrl = `api/reports/Pavement?county=${this.crsList[this.aoiIndex].countyCode}&route=${this.crsList[this.aoiIndex].stateRouteNumber}&segment=${this.crsList[this.aoiIndex].segmentNumber}&routeType=1`;

    try {
      const data: any = await this.http.get<any>(requestUrl).toPromise();

      // Determine the pavement type based on the data retrieved and show respective table
      // Pavement types: Bituminous, concrete, reinforced, unpaved
      if (data) {
        this.pavementData = data;

        if (this.pavementData.bitRoughAvg) {
          this.pavementType = 'Bituminous';
        } else if (this.pavementData.concRoughAvg) {
          this.pavementType = 'Concrete';
        } else if (this.pavementData.crcRoughAvg) {
          this.pavementType = 'Reinforced';
        } else if (this.pavementData.upCorrugation) {
          this.pavementType = 'Unpaved';
        } else {
          this.pavementType = 'Bituminous';
        }
      }

      this.isLoading = false;
    } catch (error) {
      console.error('Error', error);
      this.isLoading = false;
    }
  }
  async getShoulderData() {
    this.isLoading = true;
    const requestUrl = `api/reports/Shoulder?county=${this.crsList[this.aoiIndex].countyCode}&route=${this.crsList[this.aoiIndex].stateRouteNumber}&segment=${this.crsList[this.aoiIndex].segmentNumber}&routeType=1`;

    try {
      const data: any = await this.http.get<any>(requestUrl).toPromise();

      this.shoulderData = data;
      this.isLoading = false;
    } catch (error) {
      console.error('Error', error);
      this.isLoading = false;
    }
  }
  async getGuiderailData() {
    this.isLoading = true;
    const requestUrl = `api/reports/GDRail?county=${this.crsList[this.aoiIndex].countyCode}&route=${this.crsList[this.aoiIndex].stateRouteNumber}&segment=${this.crsList[this.aoiIndex].segmentNumber}&routeType=1`;

    try {
      const data: any = await this.http.get<any>(requestUrl).toPromise();

      this.guiderailData = data;
      this.isLoading = false;
    } catch (error) {
      console.error('Error', error);
      this.isLoading = false;
    }
  }
  async getSegmentData() {
    this.isLoading = true;
    var selectedYear: number = this.videoLogController.nativeElement.VideoLogService.currentYear;
    var latestYear: number = this.videoLogController.nativeElement.VideoLogService.yearRange.years[this.videoLogController.nativeElement.VideoLogService.yearRange.yearIndexMax];
    if (selectedYear < latestYear) {
      var requestUrl = "api/reports/SegmentHist?county=" + this.crsList[this.aoiIndex].countyCode + "&route=" + this.crsList[this.aoiIndex].stateRouteNumber
        + "&segment=" + this.crsList[this.aoiIndex].segmentNumber + "&year=" + selectedYear.toString();
    } else {
      var requestUrl = "api/reports/Segment?county=" + this.crsList[this.aoiIndex].countyCode + "&route=" + this.crsList[this.aoiIndex].stateRouteNumber
        + "&segment=" + this.crsList[this.aoiIndex].segmentNumber;
    }

    try {
      const data: any[] = await this.http.get<any>(requestUrl).toPromise();

      for (let i = 0; i < data.length; i++) {
        const year = data[i].date.slice(0, 4);
        const month = data[i].date.slice(4, 6);
        const day = data[i].date.slice(6, 8);
        data[i].date = `${month}/${day}/${year}`;
        const latArray: string[] = data[i].latSig.split(':');
        const longArray: string[] = data[i].longSig.split(':');
        data[i].latSig = (this.convertDMSToDD(parseFloat(latArray[0]), parseFloat(latArray[1]), parseFloat(latArray[2]))).toFixed(6);
        data[i].longSig = (this.convertDMSToDD(parseFloat(longArray[0]), parseFloat(longArray[1]), parseFloat(longArray[2]))).toFixed(6);
      }

      this.segmentData = data;
      this.segmentTitle = `${data[0].county} | ${data[0].route} | ${data[0].segment}`;
      this.isLoading = false;
    } catch (error) {
      console.error('Error', error);
      this.isLoading = false;
    }
  }

  async clickOffset(i: number) {
    let curYear: number = this.videoLogController.nativeElement.VideoLogService.currentYear ? this.videoLogController.nativeElement.VideoLogService.currentYear : 0;
    var crsArray: string[] = this.segmentTitle.split(' | ');
    var crs = { countyCode: crsArray[0], stateRouteNumber: crsArray[1], segmentNumber: crsArray[2], offset: this.segmentData[i].offset };
    await this.videoLogViewer.nativeElement.setCrs(crs);
    this.videoLogController.nativeElement.VideoLogService.currentYear = curYear;
  }
  convertDMSToDD(degrees: number, minutes: number, seconds: number): number {
    return degrees + minutes / 60 + seconds / (60 * 60);
  } 
  

  log(message: string, level: number, isPrivate: boolean) {
    if (level > 6) {
      let applicationMessage = new CustomEvent('applicationMessage', { detail: { message: 'Something very bad happened, please refresh!', level: level, isPrivate: isPrivate ? isPrivate : false }, composed: true });
      window.dispatchEvent(applicationMessage);
    } else {
      let applicationMessage = new CustomEvent('applicationMessage', { detail: { message: message, level: level, isPrivate: isPrivate ? isPrivate : false }, composed: true });
      window.dispatchEvent(applicationMessage);
    }
  };

  sleep(seconds: number) {
    return new Promise((resolve) => {
      setTimeout(resolve, seconds);
    });
  }

  closeEndSegWindow() {
    this.endSegmentWarningWindow.nativeElement.close();
  }

  closeYearWarningWindow() {
    this.yearWarningWindow.nativeElement.close();
  }
  
  async goToNextSeg() {
    //send event to video-log-controller to advance to next segement
    await this.videoLogController.nativeElement.VideoLogService.goToNextSegment();
    this.videoLogController.nativeElement.VideoLogService.resumeVideo();
    this.endSegmentWarningWindow.nativeElement.close();
  }
  closeEndCtyWindow() {
    this.endCountyWarningWindow.nativeElement.close();
  }
  async goToNextCty() {
    //send event to video-log-controller to advance to next county
    await this.videoLogController.nativeElement.VideoLogService.goToNextCounty();
    this.videoLogController.nativeElement.VideoLogService.resumeVideo();
    this.endCountyWarningWindow.nativeElement.close();
  }
  closeBgnSegWindow() {
    this.beginSegmentWarningWindow.nativeElement.close();
  }
  async goToPrevSeg() {
    //send event to video-log-controller to advance to next segement
    await this.videoLogController.nativeElement.VideoLogService.goToPreviousSegment();
    this.videoLogController.nativeElement.VideoLogService.resumeVideo();
    this.beginSegmentWarningWindow.nativeElement.close();
  }
  closeBgnCtyWindow() {
    this.beginCountyWarningWindow.nativeElement.close();
  }
  closeHistoricReportNoticeWindow() {
    this.historicReportNoticeWindow.nativeElement.close();
  }
  
  async goToPrevCty() {
    //send event to video-log-controller to advance to next county
    await this.videoLogController.nativeElement.VideoLogService.goToPreviousCounty();
    this.videoLogController.nativeElement.VideoLogService.resumeVideo();
    this.beginCountyWarningWindow.nativeElement.close();
  }

  swapPanels() {
    let mapContent = document.getElementById("mapContent");
    let imageContent = document.getElementById("imageContent");
    let topLeftPanel = document.getElementById("topLeftPanel");
    let bottomPanel = document.getElementById("bottomPanel");

    if (topLeftPanel.contains(imageContent)) {
      topLeftPanel.appendChild(mapContent);
      bottomPanel.appendChild(imageContent);
    } else {
      topLeftPanel.appendChild(imageContent);
      bottomPanel.appendChild(mapContent);
    }
  }

  showHistoricReportNotice(year: number = -1) {
    if (year != -1) {
      this.historicRptYear = year;
      this.showHistoricRptWarningYear = true;
    } else {
      this.showHistoricRptWarningYear = false;
    }
    this.historicReportNoticeWindow.nativeElement.open();
  }

  toggleMileMarkers() {
    this.showMileMarkers = !this.showMileMarkers;
    document.getElementById("mileMarkerButton").title = this.showMileMarkers ? "Hide Mile Markers" : "Show Mile Markers";
    if (this.showMileMarkers) {
      const layerSettings = [{ id: this.config.mileMarkerLayerId }]
      this.layerService.addMapcoreLayersToMap(layerSettings);

    } else {
      // remove layer
      const layerArray = this.mapRef.nativeElement.map.layers.toArray();
      const mileMarkerLayer = layerArray.filter(l => l.mapcoreProps && l.mapcoreProps.parentId && l.mapcoreProps.parentId == this.config.mileMarkerLayerParentId)[0];
      this.mapRef.nativeElement.map.remove(mileMarkerLayer);
    }
  }

  buildBasicRptExportData() {
    let videoLogData = this.videoLogController.nativeElement.VideoLogService.videoLog;
    let imageData = this.videoLogController.nativeElement.VideoLogService.image;
    return {
      county: videoLogData.countyName + videoLogData.countyCode,
      route: videoLogData.stateRouteNumber + videoLogData.routeName,
      type: this.jurisMap[videoLogData.juris],
      direction: videoLogData.direction,
      segment: videoLogData.segmentNumber,
      offset: imageData.offset,
      segmentLength: videoLogData.segmentLength,
      latitude: imageData.latitude,
      longitude: imageData.longitude,
      name: imageData.imageName,
      date: imageData.imageDate
    }
  }
}
